import path from 'node:path';
import { existsSync } from 'node:fs';
import { fileURLToPath } from 'node:url';
import { Command } from 'commander';
import express from 'express';
import chalk from 'chalk';
import output from '../utils/output-writer';
import api from '../services/api';

// Resolves the static UI dist directory across three runtime contexts:
//   1. tsup ESM build (build/index.js): import.meta.url is a valid file URL.
//   2. esbuild CJS bundle for pkg binary: esbuild emits `import_meta = {}`
//      (no URL), but CJS __dirname is defined and points into the pkg snapshot.
//   3. tsx dev (src/commands/ui.ts): same as ESM build.
// eslint-disable-next-line no-underscore-dangle
declare const __dirname: string;

function resolveDistPath(): string {
  const moduleUrl = import.meta.url;
  const moduleDir = moduleUrl
    ? path.dirname(fileURLToPath(moduleUrl))
    : __dirname;

  const candidates = [
    // pkg snapshot: /snapshot/<pkg>/dist/binary/ -> /snapshot/<pkg>/build/ui
    // tsx dev:      <repo>/src/commands/        -> <repo>/build/ui
    path.resolve(moduleDir, '..', '..', 'build', 'ui'),
    // tsup ESM build: build/index.js -> build/ui
    path.join(moduleDir, 'ui'),
  ];
  // Probe for the entry file rather than the directory: pkg's snapshot
  // virtual fs reports files (matched by pkg.assets glob) but not always
  // the parent directory.
  const found = candidates.find((p) => existsSync(path.join(p, 'index.html')));
  return found ?? candidates[candidates.length - 1];
}

interface UiOptions {
  port: string;
  host: string;
}

const ui = new Command('ui')
  .description('Serve the Reliability Center web UI on localhost')
  .option('-p, --port <port>', 'Port to listen on', '4321')
  .option('-h, --host <host>', 'Host to bind to', '127.0.0.1')
  .action(async (opts: UiOptions) => {
    const port = Number.parseInt(opts.port, 10);
    if (!Number.isFinite(port) || port <= 0 || port > 65535) {
      output.error(chalk.red(`Invalid port: ${opts.port}`));
      process.exit(1);
    }

    try {
      const { enabled } = await api.getReliabilityCenterAccess();
      if (!enabled) {
        output.error(
          chalk.red('The Reliability Center is not enabled for your account.')
        );
        output.log(chalk.gray('Contact Wild Moose to request access.'));
        process.exit(1);
      }
    } catch (err) {
      output.error(chalk.red('Could not verify Reliability Center access.'));
      output.error(
        chalk.gray(err instanceof Error ? err.message : String(err))
      );
      output.log(
        chalk.gray(
          'If you are not logged in, run `npx @wild-moose/moose-cli@latest login` first.'
        )
      );
      process.exit(1);
    }

    const distPath = resolveDistPath();
    const app = express();
    app.use(express.static(distPath, { index: 'index.html' }));
    app.get(/.*/, (_req, res) => {
      res.sendFile('index.html', { root: distPath });
    });

    const server = app.listen(port, opts.host, () => {
      const url = `http://${opts.host}:${opts.port}`;
      output.info(chalk.green(`✓ Reliability Center running at ${url}`));
      output.log(
        chalk.gray(
          `  Serving from ${path.relative(process.cwd(), distPath) || distPath}`
        )
      );
      output.log(chalk.gray('  Press Ctrl+C to stop.'));
    });

    server.on('error', (err: Error & { code?: string }) => {
      if (err.code === 'EADDRINUSE') {
        output.error(chalk.red(`Port ${opts.port} is already in use.`));
      } else {
        output.error(chalk.red('Server error:'), err.message);
      }
      process.exit(1);
    });
  });

export default ui;
